import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ChangePasswordBody } from '@tasks/contracts';

import { changePassword } from '../../api/auth';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { humanizeError } from '../../lib/errors';
import styles from './ChangePasswordPage.module.css';

/** UC-AUTH-06: смена пароля. Обязательна, если `mustChangePassword`. */
export function ChangePasswordPage() {
  const { user, isLoading, setUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', repeat: '' });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: ChangePasswordBody) => changePassword(body),
    onSuccess: () => {
      if (user) {
        setUser({ ...user, mustChangePassword: false });
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
      toast.success('Пароль изменён');
      navigate('/', { replace: true });
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      setError(human.message);
    },
  });

  if (!isLoading && !user) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    if (form.newPassword.length < 8) {
      setError('Пароль не короче 8 символов');
      return;
    }
    if (form.newPassword !== form.repeat) {
      setError('Пароли не совпадают');
      return;
    }
    mutation.mutate({ currentPassword: form.currentPassword, newPassword: form.newPassword });
  };

  const isForced = user?.mustChangePassword ?? false;

  return (
    <main className={styles['screen']}>
      <form className={styles['card']} onSubmit={handleSubmit} noValidate>
        <h1 className={styles['title']}>Смена пароля</h1>
        <p className={styles['subtitle']}>
          {isForced
            ? 'Пароль выдал администратор. Задайте свой, чтобы продолжить.'
            : 'После смены остальные сессии будут завершены.'}
        </p>

        <Input
          label="Текущий пароль"
          name="currentPassword"
          type="password"
          autoComplete="current-password"
          autoFocus
          value={form.currentPassword}
          onChange={(event) => setForm((prev) => ({ ...prev, currentPassword: event.target.value }))}
        />
        <Input
          label="Новый пароль"
          name="newPassword"
          type="password"
          autoComplete="new-password"
          hint="Не короче 8 символов"
          value={form.newPassword}
          onChange={(event) => setForm((prev) => ({ ...prev, newPassword: event.target.value }))}
        />
        <Input
          label="Повторите пароль"
          name="repeat"
          type="password"
          autoComplete="new-password"
          value={form.repeat}
          onChange={(event) => setForm((prev) => ({ ...prev, repeat: event.target.value }))}
        />

        {error ? (
          <p className={styles['error']} role="alert">
            {error}
          </p>
        ) : null}

        <Button type="submit" variant="primary" fullWidth loading={mutation.isPending}>
          Сменить пароль
        </Button>
      </form>
    </main>
  );
}
